import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Droplet, Plus, Trash2, Pencil, X } from "lucide-react";
import { api, errMsg, fmtDate, todayISO } from "@/lib/api";
import { Field, StatCard, EmptyState } from "@/components/Field";

const blank = () => ({ date: todayISO(), count: "", location: "", notes: "" });

export default function BloodDraws() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [items, setItems] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [f, setF] = useState(blank());
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/blood-draws", { params: { month } }).then((r) => setItems(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/blood-draws").then((r) => setSedi([...new Set(r.data.map((d) => d.location))])); }, [items]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, count: Number(f.count) };
      editId ? await api.put(`/blood-draws/${editId}`, body) : await api.post("/blood-draws", body);
      toast.success(editId ? "Registrazione aggiornata" : "Prelievi registrati");
      setF(blank());
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la registrazione?")) return;
    await api.delete(`/blood-draws/${id}`);
    load();
  };

  const total = items.reduce((s, d) => s + d.count, 0);
  const bySede = items.reduce((acc, d) => ({ ...acc, [d.location]: (acc[d.location] || 0) + d.count }), {});

  return (
    <div className="fade-up space-y-8" data-testid="blood-draws-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Registro giornaliero</div>
          <h1 className="page-title mt-1">Prelievi</h1>
        </div>
        <Field label="Mese"><input type="month" className="field w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="blood-draws-month-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Totale prelievi del mese" value={total} icon={Droplet} testid="blood-draws-total" dark />
        <StatCard label="Giornate registrate" value={new Set(items.map((d) => d.date)).size} testid="blood-draws-days" />
        <div className="card" data-testid="blood-draws-by-sede">
          <div className="label-xs mb-3">Per sede</div>
          {Object.keys(bySede).length ? Object.entries(bySede).map(([s, n]) => (
            <div key={s} className="flex justify-between py-1 text-sm"><span className="truncate">{s}</span><span className="font-bold">{n}</span></div>
          )) : <span className="text-sm text-slate-400">—</span>}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={save} className="card h-fit space-y-4" data-testid="blood-draw-form">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">{editId ? "Modifica registrazione" : "Registra prelievi"}</h3>
            {editId && <button type="button" className="btn-ghost" onClick={() => { setEditId(null); setF(blank()); }} data-testid="blood-draw-cancel-edit"><X className="h-4 w-4" /></button>}
          </div>
          <Field label="Data"><input type="date" required className="field" value={f.date} onChange={set("date")} data-testid="blood-draw-date-input" /></Field>
          <Field label="Numero prelievi"><input type="number" min="0" required className="field" value={f.count} onChange={set("count")} data-testid="blood-draw-count-input" /></Field>
          <Field label="Sede">
            <input required list="sedi-list" className="field" value={f.location} onChange={set("location")} placeholder="Es. Studio Torino" data-testid="blood-draw-location-input" />
            <datalist id="sedi-list">{sedi.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <Field label="Note"><input className="field" value={f.notes} onChange={set("notes")} data-testid="blood-draw-notes-input" /></Field>
          <button className="btn-primary w-full" data-testid="blood-draw-save-btn"><Plus className="h-4 w-4" /> {editId ? "Aggiorna" : "Salva"}</button>
        </form>
        <div className="lg:col-span-2">
          {items.length ? (
            <div className="overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white">
              <table className="w-full text-left text-sm" data-testid="blood-draws-table">
                <thead className="bg-[#f8fafc]"><tr>
                  <th className="label-xs px-4 py-3">Data</th><th className="label-xs px-4 py-3">Prelievi</th>
                  <th className="label-xs px-4 py-3">Sede</th><th className="label-xs hidden px-4 py-3 sm:table-cell">Note</th><th />
                </tr></thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id} className="border-t border-[#e2e8f0]" data-testid={`blood-draw-row-${d.id}`}>
                      <td className="px-4 py-3 font-semibold capitalize">{fmtDate(d.date, { weekday: "short", day: "2-digit", month: "short" })}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-[#edf2ef] px-3 py-1 font-bold text-[#2f4f47]">{d.count}</span></td>
                      <td className="px-4 py-3">{d.location}</td>
                      <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{d.notes}</td>
                      <td className="whitespace-nowrap px-2 text-right">
                        <button className="btn-ghost" onClick={() => { setEditId(d.id); setF({ date: d.date, count: d.count, location: d.location, notes: d.notes || "" }); }} data-testid={`blood-draw-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                        <button className="btn-ghost hover:text-rose-600" onClick={() => remove(d.id)} data-testid={`blood-draw-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState icon={Droplet} title="Nessun prelievo registrato in questo mese" testid="blood-draws-empty" />}
        </div>
      </div>
    </div>
  );
}
